<script setup>
// 对比度检测器（contrast-checker）。复刻 design 的 Contrast Checker.html inline <script>：
// 文本色 + 背景色（picker / HEX 双向）→ WCAG 相对亮度 → 对比度比值 → AA/AAA 通过/失败评级。
// 纯前端实时计算；可编辑预览实时反映两色搭配的可读性。单工具，mode 可选（仅占位，不分支）。
import { ref, computed } from "vue";

defineProps({
  mode: { type: String, default: "contrast-checker" }, // 单工具，保留以统一分发接口
  slug: { type: String, default: "" },
});

const fg = ref("#1A1A1A"); // 文本色 HEX
const bg = ref("#FFFFFF"); // 背景色 HEX

const valid = (h) => /^#[0-9a-fA-F]{6}$/.test(h);

// WCAG 相对亮度（sRGB → 线性化）
function lum(hex) {
  const c = hex.replace("#", "");
  if (c.length !== 6) return null;
  const v = [0, 2, 4].map((i) => {
    let x = parseInt(c.slice(i, i + 2), 16) / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * v[0] + 0.7152 * v[1] + 0.0722 * v[2];
}
function contrast(a, b) {
  const la = lum(a), lb = lum(b);
  if (la == null || lb == null) return null;
  const hi = Math.max(la, lb), lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

const ratio = computed(() => (valid(fg.value) && valid(bg.value) ? contrast(fg.value, bg.value) : null));
const ratioText = computed(() => (ratio.value ? ratio.value.toFixed(2) + ":1" : "—"));

// WCAG 阈值：普通文 AA 4.5 / AAA 7；大文 AA 3 / AAA 4.5
const grades = computed(() => {
  const r = ratio.value;
  const tests = [
    ["Normal text · AA", 4.5],
    ["Normal text · AAA", 7],
    ["Large text · AA", 3],
    ["Large text · AAA", 4.5],
  ];
  return tests.map(([label, min]) => ({ label, pass: r != null && r >= min }));
});

// picker → HEX（大写）
function onFgColor(e) { fg.value = String(e.target.value).toUpperCase(); }
function onBgColor(e) { bg.value = String(e.target.value).toUpperCase(); }
// picker 的 value 必须是合法 6 位 HEX，否则回退
const fgPicker = computed(() => (valid(fg.value) ? fg.value : "#000000"));
const bgPicker = computed(() => (valid(bg.value) ? bg.value : "#FFFFFF"));

function swap() { const t = fg.value; fg.value = bg.value; bg.value = t; }

const copied = ref(false);
async function copyResult() {
  const txt = `Text ${fg.value} on ${bg.value} — ${ratioText.value}`;
  try { if (navigator.clipboard) await navigator.clipboard.writeText(txt); } catch (_) {}
  copied.value = true; setTimeout(() => (copied.value = false), 1400);
}
</script>

<template>
  <div class="cc-wrap">
    <div class="cc-stage">
      <div class="cc-preview" :style="{ background: valid(bg) ? bg : 'var(--surface)' }">
        <div class="big" contenteditable="true" spellcheck="false" :style="{ color: valid(fg) ? fg : 'var(--tx)' }">Large heading text</div>
        <div class="small" contenteditable="true" spellcheck="false" :style="{ color: valid(fg) ? fg : 'var(--tx)' }">This is body copy used to preview how readable your text color is against the background. Good contrast keeps it legible for everyone.</div>
      </div>

      <div class="cc-panel">
        <div class="cc-field">
          <label>Text color</label>
          <div class="cc-input">
            <input type="color" :value="fgPicker" @input="onFgColor" />
            <input type="text" v-model="fg" spellcheck="false" />
          </div>
        </div>

        <button class="swapbtn" type="button" @click="swap">⇅ Swap colors</button>

        <div class="cc-field">
          <label>Background color</label>
          <div class="cc-input">
            <input type="color" :value="bgPicker" @input="onBgColor" />
            <input type="text" v-model="bg" spellcheck="false" />
          </div>
        </div>

        <div class="cc-ratio">
          <div class="num">{{ ratioText }}</div>
          <div class="lbl">Contrast ratio</div>
        </div>

        <div class="cc-grades">
          <div v-for="(g, i) in grades" :key="i" class="cc-grade" :class="g.pass ? 'cc-pass' : 'cc-fail'">
            <span>{{ g.label }}</span>
            <span class="tag">{{ g.pass ? "PASS" : "FAIL" }}</span>
          </div>
        </div>

        <button class="btn btn-outline" type="button" @click="copyResult">{{ copied ? "Copied!" : "Copy result" }}</button>
      </div>
    </div>

    <div class="trust">
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>Runs in your browser</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>Live WCAG grading</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></svg>Free forever</span>
    </div>
  </div>
</template>

<style scoped>
.cc-stage { border: 1px solid var(--line); border-radius: 22px; background: var(--surface); overflow: hidden; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
.cc-preview { padding: 44px 34px; display: flex; flex-direction: column; justify-content: center; gap: 16px; min-height: 300px; }
.cc-preview .big { font-family: var(--font-h); font-weight: 700; font-size: 30px; line-height: 1.2; overflow-wrap: break-word; word-break: break-word; outline: none; border-radius: 6px; transition: box-shadow 0.15s; }
.cc-preview .small { font-size: 15px; line-height: 1.5; overflow-wrap: break-word; word-break: break-word; outline: none; border-radius: 6px; transition: box-shadow 0.15s; }
.cc-preview [contenteditable]:hover { box-shadow: 0 0 0 1px rgba(127, 127, 127, 0.35); }
.cc-preview [contenteditable]:focus { box-shadow: 0 0 0 2px #f97316; }
.cc-panel { padding: 30px; border-left: 1px solid var(--line); display: flex; flex-direction: column; gap: 20px; }
.cc-field { display: flex; flex-direction: column; gap: 8px; }
.cc-field label { font-size: 13px; font-weight: 700; color: var(--tx-dim); }
.cc-input { display: flex; gap: 10px; align-items: center; }
.cc-input input[type="color"] { width: 46px; height: 46px; flex: none; border: 1px solid var(--line); border-radius: 11px; background: none; cursor: pointer; padding: 3px; }
.cc-input input[type="text"] { flex: 1; background: var(--surface-2); border: 1px solid var(--line); border-radius: 11px; padding: 12px 14px; color: var(--tx); font-family: var(--font-h); font-weight: 600; font-size: 15px; outline: none; }
.cc-input input[type="text"]:focus { border-color: #f97316; }
.cc-ratio { text-align: center; padding: 18px; border: 1px solid var(--line); border-radius: 14px; background: var(--surface-2); }
.cc-ratio .num { font-family: var(--font-h); font-weight: 700; font-size: 38px; color: var(--tx); }
.cc-ratio .lbl { font-size: 12px; color: var(--tx-faint); font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; margin-top: 2px; }
.cc-grades { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }
.cc-grade { display: flex; align-items: center; justify-content: space-between; padding: 11px 13px; border-radius: 10px; border: 1px solid var(--line); font-size: 13px; font-weight: 700; }
.cc-grade .tag { font-size: 11px; font-weight: 800; padding: 3px 8px; border-radius: 6px; }
.cc-pass { color: #7ee6c0; } .cc-pass .tag { background: rgba(52, 211, 153, 0.18); color: #7ee6c0; }
.cc-fail { color: #fb7185; } .cc-fail .tag { background: rgba(251, 113, 133, 0.18); color: #fb7185; }
.swapbtn { align-self: center; background: var(--surface-2); border: 1px solid var(--line); border-radius: 9px; padding: 8px 14px; color: var(--tx-dim); font-weight: 700; font-size: 13px; cursor: pointer; transition: 0.15s; }
.swapbtn:hover { color: var(--tx); border-color: #f97316; }
.trust { display: flex; gap: 22px; justify-content: center; flex-wrap: wrap; margin-top: 18px; }
.trust .i { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; color: var(--tx-dim); }
.trust .i svg { width: 16px; height: 16px; color: #f97316; }
@media (max-width: 680px) { .cc-stage { grid-template-columns: 1fr; } .cc-panel { border-left: none; border-top: 1px solid var(--line); } }
</style>
